/**
 * resolveMovimientoMetodoPago.ts — Medio de pago visible de un movimiento de caja.
 */

import type { MovimientoCaja, Venta } from "../types/caja.types";
import { isMovimientoAbonoCredito } from "./movimientoAbonoCredito";
import { findVentaForMovimiento } from "./resolveMovimientoCajaLink";

const METODO_LABEL: Record<string, string> = {
  efectivo: "Efectivo",
  nequi: "Nequi",
  daviplata: "Daviplata",
  tarjeta: "Tarjeta",
  transferencia: "Transferencia",
  mixto: "Mixto",
  credito: "Crédito",
  apertura: "Gaveta",
};

/** Slug del medio de pago; nunca devuelve vacío. */
export function resolveMovimientoMetodoPago(
  mov: MovimientoCaja,
  ventas: Venta[] = [],
): string {
  // Apertura de gaveta: no hay medio de pago.
  if (mov.tipo === "abrir_caja") return "apertura";

  const direct = mov.metodoPago?.trim();
  if (direct) return direct;

  if (mov.tipo === "devolucion" || mov.tipo === "anulacion") {
    const venta = findVentaForMovimiento(mov, ventas);
    if (venta?.metodoPago?.trim()) return venta.metodoPago.trim();
  }

  if (mov.tipo === "gasto" && mov.motivo?.startsWith("Devolución #DEV-")) {
    const venta = findVentaForMovimiento(mov, ventas);
    if (venta?.metodoPago?.trim()) return venta.metodoPago.trim();
  }

  // Salidas/entradas manuales de caja física; abonos credito sin medio explícito.
  if (
    mov.tipo === "retiro" ||
    mov.tipo === "gasto" ||
    mov.tipo === "ingreso" ||
    isMovimientoAbonoCredito(mov)
  ) {
    return "efectivo";
  }

  return "efectivo";
}

export function formatMovimientoMetodoPagoLabel(metodo: string): string {
  const key = metodo.trim().toLowerCase();
  return METODO_LABEL[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
}

export function movimientoMetodoPagoColorClass(metodo: string): string {
  const key = metodo.trim().toLowerCase();
  const map: Record<string, string> = {
    efectivo: "text-green-600 dark:text-green-400",
    nequi: "text-blue-600 dark:text-blue-400",
    daviplata: "text-red-600 dark:text-red-400",
    tarjeta: "text-purple-600 dark:text-purple-400",
    transferencia: "text-indigo-600 dark:text-indigo-400",
    mixto: "text-violet-600 dark:text-violet-400",
    credito: "text-amber-700 dark:text-amber-400",
    apertura: "text-sky-600 dark:text-sky-400",
  };
  return map[key] ?? "text-blue-600 dark:text-blue-400";
}
